import * as React from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    FormControlLabel,
    Switch,
    FormControl,
    InputLabel,
    MenuItem
} from '@mui/material';

import Select, { SelectChangeEvent } from '@mui/material/Select';

import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

import AdminLayout from '../components/AdminLayout';
import Api from '../services/Api';
import { EmojiEventsOutlined, SportsEsportsOutlined, SportsGolfOutlined } from '@mui/icons-material';

export interface IMiniGameDto {
    id: number;
    tournamentId: number;
    name: string | null;
    description: string | null;
    imageUrl: string | null;
    isActive: boolean;
}

export interface ITournamentLookup {
    id: number;
    name: string | null;
}

export interface IMiniGamesPageProps { }

export interface IMiniGamesPageState {
    miniGames: IMiniGameDto[];
    tournaments: ITournamentLookup[];
    loading: boolean;
    error: string | null;
    searchText: string;
    dialogOpen: boolean;
    dialogMode: 'create' | 'edit';
    editingMiniGame: IMiniGameDto | null;
    filterTournamentId: number | null;
}

/**
 * Diálogo para alta / edición de MiniGame
 */
interface IMiniGameDialogProps {
    open: boolean;
    mode: 'create' | 'edit';
    miniGame: IMiniGameDto | null;
    tournaments: ITournamentLookup[];
    filterTournamentId: number | null;
    onClose: () => void;
    onSaved: () => void;
}

interface IMiniGameDialogState {
    tournamentId: number | null;
    name: string;
    description: string;
    imageUrl: string;
    isActive: boolean;
    saving: boolean;
    error: string | null;
}

class MiniGameDialog extends React.Component<
    IMiniGameDialogProps,
    IMiniGameDialogState
> {
    public constructor(props: IMiniGameDialogProps) {
        super(props);

        const mg = props.miniGame;
        const initialTournamentId =
            props.filterTournamentId ?? (mg ? mg.tournamentId : null);

        this.state = {
            tournamentId: initialTournamentId,
            name: mg?.name || '',
            description: mg?.description || '',
            imageUrl: mg?.imageUrl || '',
            isActive: mg?.isActive ?? true,
            saving: false,
            error: null
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleTournamentChange = this.handleTournamentChange.bind(this);
        this.handleToggleActive = this.handleToggleActive.bind(this);
        this.handleSave = this.handleSave.bind(this);
    }

    public componentDidUpdate(prevProps: IMiniGameDialogProps): void {
        if (
            this.props.open &&
            (!prevProps.open ||
                prevProps.miniGame?.id !== this.props.miniGame?.id ||
                prevProps.mode !== this.props.mode ||
                prevProps.filterTournamentId !== this.props.filterTournamentId)
        ) {
            const mg = this.props.miniGame;
            const initialTournamentId =
                this.props.filterTournamentId ?? (mg ? mg.tournamentId : null);

            this.setState({
                tournamentId: initialTournamentId,
                name: mg?.name || '',
                description: mg?.description || '',
                imageUrl: mg?.imageUrl || '',
                isActive: mg?.isActive ?? true,
                saving: false,
                error: null
            });
        }
    }

    private validate(): string | null {
        const { tournamentId, name } = this.state;

        if (!tournamentId) return 'El torneo es obligatorio.';
        if (!name.trim()) return 'El nombre es obligatorio.';

        return null;
    }

    private handleChange(
        field: 'name' | 'description' | 'imageUrl'
    ) {
        return (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            this.setState({
                [field]: event.target.value
            } as Pick<IMiniGameDialogState, typeof field>);
        };
    }

    private handleTournamentChange(event: SelectChangeEvent): void {
        const value = event.target.value;
        this.setState({
            tournamentId: value ? Number(value) : null
        });
    }

    private handleToggleActive(
        event: React.ChangeEvent<HTMLInputElement>,
        checked: boolean
    ): void {
        this.setState({ isActive: checked });
    }

    private async handleSave(): Promise<void> {
        const error = this.validate();
        if (error) {
            this.setState({ error });
            return;
        }

        this.setState({ saving: true, error: null });

        try {
            const { mode, miniGame } = this.props;
            const { tournamentId, name, description, imageUrl, isActive } = this.state;

            const body = {
                tournamentId,
                name,
                description,
                imageUrl,
                isActive
            };

            if (mode === 'create') {
                const response = await Api.post('/api/MiniGames', body);
                if (!response.ok) {
                    throw new Error('No se pudo crear el juego.');
                }
            } else {
                if (!miniGame) {
                    throw new Error('No se ha especificado el juego a editar.');
                }
                const response = await Api.put(`/api/MiniGames/${miniGame.id}`, body);
                if (!response.ok) {
                    throw new Error('No se pudo actualizar el juego.');
                }
            }

            this.props.onSaved();
        } catch (err: any) {
            this.setState({
                saving: false,
                error: err.message || 'Error al guardar el juego.'
            });
        }
    }

    private getTournamentNameById(id: number | null): string {
        if (!id) return '';
        const t = this.props.tournaments.find((x) => x.id === id);
        return t?.name || `Torneo ${id}`;
    }

    public render(): React.ReactElement<IMiniGameDialogProps> {
        const { open, onClose, mode, filterTournamentId, tournaments } = this.props;
        const {
            tournamentId,
            name,
            description,
            imageUrl,
            isActive,
            saving,
            error
        } = this.state;

        const title = mode === 'create' ? 'Nuevo juego' : 'Editar juego';

        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>{title}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {error && (
                            <Typography variant="body2" color="error">
                                {error}
                            </Typography>
                        )}

                        {filterTournamentId === null && (
                            <FormControl fullWidth>
                                <InputLabel id="tournament-label">Torneo</InputLabel>
                                <Select
                                    labelId="tournament-label"
                                    label="Torneo"
                                    value={tournamentId !== null ? String(tournamentId) : ''}
                                    onChange={this.handleTournamentChange}
                                >
                                    {tournaments.map((t) => (
                                        <MenuItem key={t.id} value={t.id.toString()}>
                                            {t.name || `Torneo ${t.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {filterTournamentId !== null && (
                            <TextField
                                label="Torneo"
                                fullWidth
                                value={this.getTournamentNameById(filterTournamentId)}
                                disabled
                            />
                        )}

                        <TextField
                            label="Nombre"
                            fullWidth
                            value={name}
                            onChange={this.handleChange('name')}
                            required
                        />

                        <TextField
                            label="Descripción"
                            fullWidth
                            multiline
                            minRows={3}
                            value={description}
                            onChange={this.handleChange('description')}
                        />

                        <TextField
                            label="URL de imagen"
                            fullWidth
                            value={imageUrl}
                            onChange={this.handleChange('imageUrl')}
                            placeholder="https://..."
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isActive}
                                    onChange={this.handleToggleActive}
                                    color="primary"
                                />
                            }
                            label="juego activo"
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={this.handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Guardando...' : 'Guardar'}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }
}

export default class MiniGamesPage extends React.Component<
    IMiniGamesPageProps,
    IMiniGamesPageState
> {
    public constructor(props: IMiniGamesPageProps) {
        super(props);

        const params = new URLSearchParams(window.location.search);
        const tid = params.get('tournamentId');
        const filterTournamentId =
            tid && !isNaN(parseInt(tid, 10)) ? parseInt(tid, 10) : null;

        this.state = {
            miniGames: [],
            tournaments: [],
            loading: false,
            error: null,
            searchText: '',
            dialogOpen: false,
            dialogMode: 'create',
            editingMiniGame: null,
            filterTournamentId
        };

        this.loadMiniGames = this.loadMiniGames.bind(this);
        this.handleCreate = this.handleCreate.bind(this);
        this.handleEdit = this.handleEdit.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleSearchChange = this.handleSearchChange.bind(this);
        this.handleDialogClose = this.handleDialogClose.bind(this);
        this.handleDialogSaved = this.handleDialogSaved.bind(this);
        this.goToMinigame = this.goToMinigame.bind(this);
    }

    public componentDidMount(): void {
        this.loadMiniGames();
    }

    private async loadMiniGames(): Promise<void> {
        this.setState({ loading: true, error: null });

        try {
            const { filterTournamentId } = this.state;

            if (filterTournamentId) {
                const [mgResponse, tResponse] = await Promise.all([
                    Api.get(`/api/MiniGames/by-tournament/${filterTournamentId}`),
                    Api.get(`/api/Tournaments/${filterTournamentId}`)
                ]);

                if (!mgResponse.ok) {
                    throw new Error('No se pudieron cargar los juegos.');
                }
                if (!tResponse.ok) {
                    throw new Error('No se pudo obtener la información del torneo.');
                }

                const miniGames: IMiniGameDto[] = await mgResponse.json();
                const tData: any = await tResponse.json();
                const tournaments: ITournamentLookup[] = [
                    { id: tData.id, name: tData.name }
                ];

                this.setState({
                    miniGames,
                    tournaments,
                    loading: false,
                    error: null,
                    searchText: ''
                });
            } else {
                const [mgResponse, tResponse] = await Promise.all([
                    Api.get('/api/MiniGames'),
                    Api.get('/api/Tournaments')
                ]);

                if (!mgResponse.ok) {
                    throw new Error('No se pudieron cargar los juegos.');
                }
                if (!tResponse.ok) {
                    throw new Error('No se pudieron cargar los torneos.');
                }

                const miniGames: IMiniGameDto[] = await mgResponse.json();
                const tData: any[] = await tResponse.json();

                const tournaments: ITournamentLookup[] = tData.map((t) => ({
                    id: t.id,
                    name: t.name
                }));

                this.setState({
                    miniGames,
                    tournaments,
                    loading: false,
                    error: null,
                    searchText: ''
                });
            }
        } catch (error: any) {
            this.setState({
                loading: false,
                error: error.message || 'Error al cargar los juegos.'
            });
        }
    }

    private handleCreate(): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'create',
            editingMiniGame: null
        });
    }

    private handleEdit(miniGame: IMiniGameDto): void {
        this.setState({
            dialogOpen: true,
            dialogMode: 'edit',
            editingMiniGame: miniGame
        });
    }

    private async handleDelete(miniGame: IMiniGameDto): Promise<void> {
        const confirmar = window.confirm(
            `¿Seguro que deseas eliminar el juego "${miniGame.name}"?`
        );
        if (!confirmar) {
            return;
        }

        try {
            const response = await Api.delete(`/api/MiniGames/${miniGame.id}`);

            if (!response.ok) {
                throw new Error('No se pudo eliminar el juego.');
            }

            await this.loadMiniGames();
        } catch (error: any) {
            alert(error.message || 'Error al eliminar el juego.');
        }
    }

    private goToMinigame(miniGame: IMiniGameDto): void {
        // De momento navegación simple, luego ajustamos la ruta real
        window.location.href = `/game?gameId=${miniGame.id}`;
    }

    private handleSearchChange(
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void {
        this.setState({ searchText: event.target.value });
    }

    private getTournamentNameFor(miniGame: IMiniGameDto): string {
        const { tournaments } = this.state;
        const t = tournaments.find((x) => x.id === miniGame.tournamentId);
        return t?.name || `Torneo ${miniGame.tournamentId}`;
    }

    private matchesSearch(mg: IMiniGameDto, term: string): boolean {
        const q = term.trim().toLowerCase();
        if (!q) return true;

        const parts: string[] = [];

        if (mg.name) parts.push(mg.name);
        if (mg.description) parts.push(mg.description);
        if (mg.imageUrl) parts.push(mg.imageUrl);

        // Nombre del torneo también entra en la búsqueda
        const tName = this.getTournamentNameFor(mg);
        if (tName) parts.push(tName);

        parts.push(mg.isActive ? 'si sí activo true' : 'no inactivo false');

        return parts.some((p) => p.toLowerCase().includes(q));
    }

    private handleDialogClose(): void {
        this.setState({ dialogOpen: false, editingMiniGame: null });
    }

    private async handleDialogSaved(): Promise<void> {
        this.setState({ dialogOpen: false, editingMiniGame: null });
        await this.loadMiniGames();
    }

    private renderToolbar(): React.ReactNode {
        const { searchText } = this.state;

        return (
            <>
                <Button
                    variant="contained"
                    size='small'
                    startIcon={<AddIcon />}
                    onClick={this.handleCreate}
                >
                    Nuevo
                </Button>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar juego..."
                    value={searchText}
                    onChange={this.handleSearchChange}
                    sx={{
                        ml: 'auto',
                        minWidth: { xs: '100%', sm: 220, md: 260 },
                        mt: { xs: 1, sm: 0 }
                    }}
                />
            </>
        );
    }

    private renderMiniGameCard(miniGame: IMiniGameDto): React.ReactNode {
        return (
            <Grid key={miniGame.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                    sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: 4
                    }}
                >
                    {miniGame.imageUrl ? (
                        <CardMedia
                            component="img"
                            height="160"
                            image={miniGame.imageUrl}
                            alt={miniGame.name || 'MiniGame image'}
                        />
                    ) : (
                        <Box
                            sx={{
                                height: 160,
                                bgcolor: 'grey.300',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <SportsEsportsIcon sx={{ fontSize: 48, color: 'grey.600' }} />
                        </Box>
                    )}

                    <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" fontWeight={500} gutterBottom noWrap>
                            {miniGame.name || 'Sin nombre'}
                        </Typography>

                        {miniGame.description && (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                {miniGame.description}
                            </Typography>
                        )}

                        <Box sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                                {this.getTournamentNameFor(miniGame)}
                            </Typography>
                            <Typography
                                variant="caption"
                                color={miniGame.isActive ? 'success.main' : 'text.secondary'}
                                sx={{ ml: 1 }}
                            >
                                {miniGame.isActive ? 'Activo' : 'Inactivo'}
                            </Typography>
                        </Box>
                    </CardContent>

                    <CardActions>
                        <Button
                            size="small"
                            onClick={() => this.handleEdit(miniGame)}
                        >
                            Editar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => this.handleDelete(miniGame)}
                        >
                            Eliminar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => this.goToMinigame(miniGame)}
                        >
                            Acceder
                        </Button>
                    </CardActions>
                </Card>
            </Grid>
        );
    }

    private renderGrid(): React.ReactNode {
        const {
            miniGames,
            loading,
            error,
            searchText,
            filterTournamentId
        } = this.state;

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return (
                <Typography color="error" variant="body1">
                    {error}
                </Typography>
            );
        }

        if (!miniGames || miniGames.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay juegos todavía.
                </Typography>
            );
        }

        const filtered = miniGames.filter((mg) =>
            this.matchesSearch(mg, searchText)
        );

        if (filtered.length === 0) {
            return (
                <Typography variant="body1" color="text.secondary">
                    No hay juegos que coincidan con la búsqueda.
                </Typography>
            );
        }

        // Si hay torneo filtrado por querystring → un solo bloque
        if (filterTournamentId) {
            return (
                <Grid container spacing={3}>
                    {filtered.map((mg) => this.renderMiniGameCard(mg))}
                </Grid>
            );
        }

        // Sin torneo filtrado → agrupamos por torneo
        const groups = new Map<number, IMiniGameDto[]>();
        filtered.forEach((mg) => {
            const list = groups.get(mg.tournamentId) || [];
            list.push(mg);
            groups.set(mg.tournamentId, list);
        });

        return (
            <>
                {Array.from(groups.entries()).map(([tournamentId, list]) => {
                    const tournamentName = this.getTournamentNameFor(list[0]);

                    return (
                        <Box key={tournamentId} sx={{ mb: 4 }}>
                            <Stack direction={'row'} justifyContent={'flex-start'} alignItems={'center'} gap={'5px'} sx={{
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                mb: 2,
                                pb: 0.5
                            }}>
                                <EmojiEventsOutlined />
                                <Typography
                                    variant="h6"
                                >
                                    {tournamentName}
                                </Typography>
                            </Stack>

                            <Grid container spacing={3}>
                                {list.map((mg) => this.renderMiniGameCard(mg))}
                            </Grid>
                        </Box>
                    );
                })}
            </>
        );
    }

    public render(): React.ReactElement<IMiniGamesPageProps> {
        const {
            dialogOpen,
            dialogMode,
            editingMiniGame,
            tournaments,
            filterTournamentId
        } = this.state;

        return (
            <>
                <AdminLayout
                    entityTitle="Juegos"
                    entityIcon={<SportsGolfOutlined sx={{ fontSize: 56 }} />}
                    entityToolbar={this.renderToolbar()}
                >
                    {this.renderGrid()}
                </AdminLayout>

                <MiniGameDialog
                    open={dialogOpen}
                    mode={dialogMode}
                    miniGame={editingMiniGame}
                    tournaments={tournaments}
                    filterTournamentId={filterTournamentId}
                    onClose={this.handleDialogClose}
                    onSaved={this.handleDialogSaved}
                />
            </>
        );
    }
}
